import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Cookies from "js-cookie";
import { CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { FleetrifyBgs } from "../../../assets/video";
import CarouselCard from "../../../components/Carousel";
import { FleetrifyButton } from "../../../components/Button/customButton";
import FleetrifyOTPInput from "../../../components/Input/Otp";
import { useVerifyEmail } from "../../../controllers/useVerifyEmail";
import { FLEETRIFYROUTEPATH } from "../../../routes/routesPath";
import { selectAuthUser } from "../../../redux/slices/authSlice";

const slides = [
  { title: "Welcome to Fleetrify", description: "Login to manage logistics and fleet operations" },
  { title: "Real-time Fleet Insights", description: "Get live updates and analytics on every vehicle in your fleet" },
  { title: "Simplify Logistics Management", description: "Automate routing, tracking, and reporting — all in one platform" },
];

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;

  const email = state?.email;
  const mode: "signup" | "reset" | "create-account" = state?.mode || "signup";
  // Returning sign-up (registered before, never verified). /verify-token now hands them a
  // session too, so they carry on from where they stopped like any new sign-up.
  const isReturning = state?.returning === true;

  const {
    otp,
    setOtp,
    message,
    messageType,
    startCountdown,
    isCounting,
    secondsLeft,
    isDone,
    isResending,
    handleContinue,
    normalizedMode,
    showSuccess,
    nextStep,
  } = useVerifyEmail(email, mode);

  const auth = useSelector(selectAuthUser);
  const userId = Cookies.get("signup_user_id") || auth.user?.id;

  // acc types
  const accountTypeFromState = state?.account_type;
  const accountType = accountTypeFromState || auth.user?.account_type;

  //redirect after successful verification
  useEffect(() => {
    if (!showSuccess || normalizedMode !== "signup") return;

    // /verify-token may have just saved the user, so read the type from there first.
    const isCompany = (auth.user?.account_type || accountType)?.toLowerCase() === "company";
    const setupPath = `${isCompany ? FLEETRIFYROUTEPATH.COMPANYSETUP : FLEETRIFYROUTEPATH.INDIVIDUALSETUP}?userId=${userId}`;

    // The API says where to go: company_setup | kyc | login.
    if (nextStep === "company_setup") {
      navigate(setupPath);
      return;
    }
    if (nextStep === "kyc") {
      // KYC for an existing company is done from the dashboard popup, which needs the
      // full login data (company, subscription, permissions) — /verify-token doesn't send it.
      // (The onboarding KYC form re-submits the whole company profile, so it's not used here.)
      toast.success("Email verified. Log in to finish verifying your business (KYC).");
      navigate(FLEETRIFYROUTEPATH.LOGIN, { state: { email } });
      return;
    }
    if (nextStep === "login") {
      navigate(FLEETRIFYROUTEPATH.LOGIN, { state: { email } });
      return;
    }

    // No next_step (older API): returning users have no token, so they log in.
    if (isReturning) {
      navigate(FLEETRIFYROUTEPATH.LOGIN, { state: { email } });
    } else if (accountType?.toLowerCase() === "company" || accountType?.toLowerCase() === "individual") {
      navigate(setupPath);
    } else {
      navigate(FLEETRIFYROUTEPATH.LOGIN);
    }
  }, [showSuccess, normalizedMode, accountType, auth.user?.account_type, navigate, userId, isReturning, email, nextStep]);

  if (!email) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 px-4">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-sm text-center">
          <div className="flex items-center justify-center mb-4">
            <CheckCircle className="text-red-500 w-12 h-12" />
          </div>
          <h2 className="text-xl font-bold text-red-500 mb-2">Oops!</h2>
          <p className="text-gray-600 mb-6">
            Email is missing. Cannot verify your account.
          </p>
          <button
            onClick={() => navigate(FLEETRIFYROUTEPATH.FORGOT_PASSWORD)}
            className="bg-[#143887] text-white px-4 py-2 rounded-md w-full hover:bg-[#1a4aa0] transition"
          >
            Go to Forgot Password
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col lg:flex-row min-h-screen items-center justify-center lg:bg-transparent bg-gray-100">
      {/* video section */}
      <div className="relative w-full lg:w-[45%] h-screen lg:h-screen order-2 lg:order-none">
        <video className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline>
          <source src={FleetrifyBgs} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black opacity-50 z-10"></div>
        <div className="absolute z-20 inset-0 flex items-center justify-center">
          <CarouselCard slides={slides} />
        </div>
      </div>

      {/* otp form */}
      <div className="w-full lg:w-[55%] flex justify-center items-center absolute lg:static z-30 px-5 top-0 bottom-0 my-auto">
        <div className="w-[430px] sm:w-[340px] md:w-[500px] lg:w-[430px] max-w-full bg-white rounded px-4 py-6 space-y-4">
          <h2 className="text-[24px] font-bold text-center text-[#143887]">
            {normalizedMode === "signup"
              ? "EMAIL CONFIRMATION"
              : "PASSWORD RESET CONFIRMATION"}
          </h2>

          <p className="text-center text-gray-600">
            {normalizedMode === "signup"
              ? "A verification token has been sent to"
              : "An OTP has been sent to"} <strong>{email}</strong>.
          </p>

          {isReturning && normalizedMode === "signup" && (
            <div className="rounded-lg border border-[#D5DEF3] bg-[#EEF2FB] px-4 py-3 text-sm text-[#143887]">
              <p className="font-semibold">Welcome back!</p>
              <p className="mt-0.5 text-[13px] leading-relaxed">
                Enter the new code we just sent to pick up where you left off.
              </p>
            </div>
          )}

          <div className="flex flex-col items-center justify-center">
            <FleetrifyOTPInput
              value={otp}
              onChange={setOtp}
              length={6}
            />
          </div>

          {/* resend otp*/}
          <div className="text-xs text-[#4F4F4F] mb-3 text-center">
            Didn't receive?{" "}
            <button
              type="button"
              className={`text-[#2F80ED] ${isCounting && !isDone ? "cursor-not-allowed" : "cursor-pointer"}`}
              onClick={startCountdown}
              disabled={isCounting && !isDone}
            >
              {isCounting && !isDone ? `Resend in ${secondsLeft}s` : isResending ? "Sending..." : "Send Code"}
            </button>
          </div>

          {/* continue button */}
          <FleetrifyButton
            className="w-full mt-4"
            onClick={handleContinue}
            disabled={otp.length !== 6}
          >
            {normalizedMode === "signup"
              ? "Verify and Continue"
              : "Verify and Reset Password"}
          </FleetrifyButton>

          {message && (
            <p className={`text-center text-sm ${messageType === "success" ? "text-green-600" : "text-red-500"}`}>{message}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
